<template>
  <div class="home-wrapper">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <h1 class="hero-title">{{ msg }}</h1>
        <p class="hero-subtitle">Créez et personnalisez vos mosaïques en pixel art</p>
        <div class="cta-buttons">
          <button class="btn btn-primary" @click="scrollToGallery">
            Découvrir
          </button>
          <button class="btn btn-secondary" @click="createNew">
            Créer maintenant
          </button>
        </div>
      </div>
    </section>

    <!-- Carousel Section -->
    <section class="carousel-section" ref="gallerySection">
      <div class="section-header">
        <h2 class="section-title">Galerie de créations</h2>
        <p class="section-subtitle">Explorez les mosaïques réalisées par notre communauté</p>
      </div>

      <div class="carousel-container">
        <button 
          class="carousel-nav prev" 
          @click="previousSlide"
          :disabled="currentSlide === 0"
        >
          ‹
        </button>

        <div class="carousel-wrapper">
          <div 
            class="carousel-track" 
            :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
          >
            <div 
              v-for="(image, index) in images" 
              :key="index" 
              class="carousel-slide"
            >
              <div class="slide-content">
                <img 
                  :src="image.src" 
                  :alt="image.alt" 
                  class="carousel-image pixel-art"
                >
                <div class="slide-info">
                  <h3>{{ image.caption }}</h3>
                  <p>{{ image.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button 
          class="carousel-nav next" 
          @click="nextSlide"
          :disabled="currentSlide === images.length - 1"
        >
          ›
        </button>
      </div>

      <!-- Carousel Indicators -->
      <div class="carousel-indicators">
        <button 
          v-for="(image, index) in images" 
          :key="index"
          :class="['indicator', { active: currentSlide === index }]"
          @click="goToSlide(index)"
        ></button>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="section-header">
        <h2 class="section-title">Fonctionnalités</h2>
      </div>
      
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon">📤</div>
          <h3 class="feature-title">Import d'image</h3>
          <p class="feature-description">
            Importez vos images et transformez-les automatiquement en pixel art
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-icon">🎨</div>
          <h3 class="feature-title">Éditeur intuitif</h3>
          <p class="feature-description">
            Créez et modifiez vos mosaïques pixel par pixel avec notre éditeur simple
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-icon">📄</div>
          <h3 class="feature-title">Export PDF</h3>
          <p class="feature-description">
            Générez des PDF de vos créations pour les imprimer ou les partager
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-icon">🔒</div>
          <h3 class="feature-title">Privé ou Public</h3>
          <p class="feature-description">
            Choisissez de garder vos créations privées ou de les partager avec la communauté
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-icon">⚙️</div>
          <h3 class="feature-title">Personnalisable</h3>
          <p class="feature-description">
            Ajustez la taille de votre mosaïque de 8x8 jusqu'à 128x128 pixels
          </p>
        </div>

        <div class="feature-card">
          <div class="feature-icon">💾</div>
          <h3 class="feature-title">Sauvegarde auto</h3>
          <p class="feature-description">
            Ne perdez jamais votre travail, toutes vos créations sont sauvegardées
          </p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-content">
        <h2>Prêt à créer votre première mosaïque ?</h2>
        <p>Rejoignez notre communauté et donnez vie à vos idées en pixel art</p>
        <button class="btn btn-cta" @click="createNew">
          Commencer maintenant
        </button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HelloWorld',
  props: {
    msg: {
      type: String,
      default: 'Bienvenue sur Mosaic Creator'
    }
  },
  data() {
    return {
      currentSlide: 0,
      autoplayInterval: null,
      images: [
        {
          src: '/images/image_dure/banane.png',
          alt: 'Mosaïque exemple 1',
          caption: 'Création géométrique',
          description: 'Design abstrait en 48x48 pixels'
        },
        {
          src: '/images/image_dure/4.png',
          alt: 'Superbe Création !',
          caption: 'Superbe Création !',
          description: 'Scène naturelle en 64x64 pixels'
        },
        {
          src: '/images/image_dure/13.png',
          alt: 'Photo Convertie en Mosaïque',
          caption: 'Photo Convertie en Mosaïque',
          description: 'Visage en 32x32 pixels'
        },
        {
          src: '/images/image_dure/6.png',
          alt: 'Mosaïque exemple 4',
          caption: 'Grande Mosaïque',
          description: 'Pattern en 128x128 pixels'
        }
      ]
    }
  },
  mounted() {
    this.startAutoplay();
  },
  beforeUnmount() {
    this.stopAutoplay();
  },
  methods: {
    nextSlide() {
      if (this.currentSlide < this.images.length - 1) {
        this.currentSlide++;
      } else {
        this.currentSlide = 0;
      }
    },
    previousSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
      } else {
        this.currentSlide = this.images.length - 1;
      }
    },
    goToSlide(index) {
      this.currentSlide = index;
    },
    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, 5000);
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
    },
    scrollToGallery() {
      this.$refs.gallerySection?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    },
    createNew() {
      // Navigation vers la page de création
      this.$router?.push('/mosaic');
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.home-wrapper {
  width: 100%;
  overflow-x: hidden;
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4dc9ff 0%, #3ab8e8 100%);
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 60%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 40px 20px;
  max-width: 900px;
  margin: 0 auto;
}

.hero-title {
  font-size: 4rem;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  text-shadow: 2px 4px 8px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.8s ease-out;
}

.hero-subtitle {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 40px;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Buttons */
.btn {
  padding: 15px 40px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: white;
  color: #4dc9ff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  background: #f8f9fa;
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

.btn-cta {
  background: white;
  color: #4dc9ff;
  padding: 18px 50px;
  font-size: 1.2rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

/* Sections */
.carousel-section,
.features-section {
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 50px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #666;
}

/* Carousel */
.carousel-container {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 60px;
}

.carousel-wrapper {
  overflow: hidden;
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  background: #f8f9fa;
}

.carousel-track {
  display: flex;
  transition: transform 0.5s ease-in-out;
}

.carousel-slide {
  min-width: 100%;
  flex-shrink: 0;
}

.slide-content {
  position: relative;
}

.carousel-image {
  width: 100%;
  height: 500px;
  object-fit: contain;
  display: block;
  background: white;
}

.pixel-art {
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.slide-info {
  background: white;
  padding: 25px;
  text-align: center;
}

.slide-info h3 {
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 10px 0;
}

.slide-info p {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 2rem;
  color: #4dc9ff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.carousel-nav:hover:not(:disabled) {
  background: white;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.carousel-nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel-nav.prev {
  left: 0;
}

.carousel-nav.next {
  right: 0;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 30px;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: #ddd;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.indicator:hover {
  background: #999;
}

.indicator.active {
  background: #4dc9ff;
  transform: scale(1.4);
}

/* Features Section */
.features-section {
  background: #f8f9fa;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.feature-card {
  background: white;
  padding: 35px 25px;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
}

.feature-icon {
  font-size: 3.5rem;
  margin-bottom: 20px;
}

.feature-title {
  font-size: 1.4rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.feature-description {
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #4dc9ff 0%, #3ab8e8 100%);
  padding: 100px 20px;
  text-align: center;
}

.cta-content {
  max-width: 700px;
  margin: 0 auto;
}

.cta-content h2 {
  font-size: 2.5rem;
  color: white;
  margin-bottom: 20px;
  font-weight: 700;
}

.cta-content p {
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 40px;
}

/* Responsive */
@media (max-width: 992px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-subtitle {
    font-size: 1.3rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .carousel-image {
    height: 400px;
  }

  .features-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 25px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    min-height: 80vh;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .btn {
    padding: 14px 30px;
    font-size: 1rem;
  }

  .carousel-section,
  .features-section {
    padding: 50px 15px;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .carousel-container {
    padding: 0 50px;
  }

  .carousel-image {
    height: 300px;
  }

  .carousel-nav {
    width: 40px;
    height: 40px;
    font-size: 1.5rem;
  }

  .slide-info {
    padding: 20px 15px;
  }

  .slide-info h3 {
    font-size: 1.2rem;
  }

  .slide-info p {
    font-size: 0.9rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .feature-card {
    padding: 30px 20px;
  }

  .cta-section {
    padding: 60px 20px;
  }

  .cta-content h2 {
    font-size: 1.8rem;
  }

  .cta-content p {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .carousel-container {
    padding: 0 40px;
  }

  .carousel-image {
    height: 250px;
  }

  .carousel-nav {
    width: 35px;
    height: 35px;
    font-size: 1.3rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .feature-icon {
    font-size: 2.8rem;
  }

  .feature-title {
    font-size: 1.2rem;
  }

  .feature-description {
    font-size: 0.9rem;
  }

  .cta-content h2 {
    font-size: 1.5rem;
  }

  .cta-content p {
    font-size: 1rem;
  }

  .btn-cta {
    padding: 15px 35px;
    font-size: 1rem;
  }
}
</style>